/* ============================================
   Proyecto — Detalle (/portal/proyectos/:id)
   Pestañas scopeadas a UN proyecto: Resumen | Línea de Tiempo | Facturación.
   Rediseño "pro": stat strip, tabla de tareas densa, right rail de cobro,
   y facturación como tabla con acordeón animado.
   ============================================ */

import { useState, useEffect, useMemo, useRef, Fragment } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { usePortalCliente } from '@/hooks/usePortalCliente'
import { supabase } from '@/lib/supabase'
import { formatCurrency, formatDate } from '@/lib/utils'
import { billingSummary, MODELO_LABEL, progresoProyecto } from '@/lib/billing'
import type { Tarea, Proyecto, TareaEvidencia } from '@/lib/types'
import { Breadcrumbs } from '@/components/ui'

const estadoOrder: Record<string, number> = { en_progreso: 0, pendiente: 1, bloqueada: 2, completada: 3 }
const estadoColors: Record<string, string> = {
  pendiente: '#636e72', en_progreso: '#0984e3', completada: '#00b894', bloqueada: '#d63031',
}
const estadoLabel: Record<string, { label: string; className: string }> = {
  pendiente: { label: 'Pendiente', className: 'badge-borrador' },
  en_progreso: { label: 'En Progreso', className: 'badge-enviada' },
  completada: { label: 'Completada', className: 'badge-aceptada' },
  bloqueada: { label: 'Bloqueada', className: 'badge-rechazada' },
}
const prioridadLabel: Record<string, { label: string; className: string }> = {
  baja: { label: 'Baja', className: 'priority-baja' },
  media: { label: 'Media', className: 'priority-media' },
  alta: { label: 'Alta', className: 'priority-alta' },
  urgente: { label: 'Urgente', className: 'priority-urgente' },
}
const proyectoEstadoBadge: Record<string, { label: string; className: string }> = {
  activo: { label: 'Activo', className: 'badge-aceptada' },
  pausado: { label: 'Pausado', className: 'badge-enviada' },
  completado: { label: 'Completado', className: 'badge-borrador' },
  cancelado: { label: 'Cancelado', className: 'badge-rechazada' },
}
// Estado de tarea → clase de la píldora densa (.pd-st) y del checkbox (.ci)
const stCls: Record<string, string> = { en_progreso: 'prog', pendiente: 'pend', completada: 'done', bloqueada: 'block' }
const stLbl: Record<string, string> = { en_progreso: 'En curso', pendiente: 'Pendiente', completada: 'Completada', bloqueada: 'Bloqueada' }
const ciCls: Record<string, string> = { en_progreso: 'prog', pendiente: '', completada: 'done', bloqueada: 'block' }

type ViewTab = 'resumen' | 'timeline' | 'facturacion'

const PER_TASKS = 15 // paginación de la tabla de tareas en Resumen

export default function ProyectoDetalle() {
  const { id } = useParams<{ id: string }>()
  const proyectoId = Number(id)
  const { activeClienteId: clienteId } = usePortalCliente()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  // El partner reutiliza esta página bajo /partner/proyectos.
  const indexPath = pathname.startsWith('/partner') ? '/partner/proyectos' : '/portal/proyectos'

  const [proyecto, setProyecto] = useState<Proyecto | null>(null)
  const [tareas, setTareas] = useState<Tarea[]>([])
  const [evidencias, setEvidencias] = useState<TareaEvidencia[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [filterEstado, setFilterEstado] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTask, setSelectedTask] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState<ViewTab>('resumen')
  const [openInv, setOpenInv] = useState<Record<string, boolean>>({})
  const [taskPage, setTaskPage] = useState(1)
  const ganttRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!clienteId || !proyectoId) return
    ;(async () => {
      setLoading(true); setNotFound(false)
      try {
        // Aislamiento en la QUERY (no solo client-side) + columnas explícitas sin campos internos.
        const { data: pData } = await supabase.from('proyectos')
          .select('id, nombre, descripcion, cliente_id, estado, fecha_inicio, fecha_fin_estimada, moneda, presupuesto, modelo_cobro')
          .eq('id', proyectoId).eq('cliente_id', clienteId).single()
        const proy = pData as unknown as Proyecto | null
        if (!proy) { setNotFound(true); setLoading(false); return }
        setProyecto(proy)

        const { data: tData } = await supabase.from('tareas')
          .select('id, proyecto_id, titulo, descripcion, estado, prioridad, fecha_limite, horas_estimadas, horas_reales, monto, factura_id, created_at')
          .eq('proyecto_id', proyectoId)
        const tasks = (tData ?? []) as unknown as Tarea[]
        setTareas(tasks)

        const taskIds = tasks.map(t => t.id)
        if (taskIds.length > 0) {
          const { data: eData } = await supabase.from('tarea_evidencias')
            .select('id, tarea_id, url, nombre, tipo, created_at')
            .in('tarea_id', taskIds)
          setEvidencias((eData ?? []) as unknown as TareaEvidencia[])
        }
      } catch { setNotFound(true) }
      finally { setLoading(false) }
    })()
  }, [clienteId, proyectoId])

  useEffect(() => { setTaskPage(1) }, [proyectoId])

  // ─── Search filter (timeline) ───
  const searchFiltered = useMemo(() => {
    if (!searchQuery.trim()) return tareas
    const q = searchQuery.toLowerCase()
    return tareas.filter(t => t.titulo.toLowerCase().includes(q) || t.descripcion?.toLowerCase().includes(q))
  }, [tareas, searchQuery])

  // ─── Facturación ───
  const facturacionData = useMemo(() => {
    const facturadas = new Map<number, Tarea[]>()
    const pendientes: Tarea[] = []
    tareas.forEach(t => {
      if (t.factura_id) {
        if (!facturadas.has(t.factura_id)) facturadas.set(t.factura_id, [])
        facturadas.get(t.factura_id)!.push(t)
      } else if (t.estado === 'completada') {
        pendientes.push(t)
      }
    })
    const bloques = Array.from(facturadas.entries()).map(([facturaId, tasks]) => ({
      facturaId,
      tasks: tasks.sort((a, b) => a.titulo.localeCompare(b.titulo)),
      total: tasks.reduce((sum, t) => sum + (t.monto ?? 0), 0),
      horas: tasks.reduce((sum, t) => sum + (t.horas_reales ?? 0), 0),
    }))
    return {
      bloques: bloques.sort((a, b) => b.facturaId - a.facturaId),
      pendientes: pendientes.sort((a, b) => a.titulo.localeCompare(b.titulo)),
    }
  }, [tareas])

  // ─── Métricas de cobro ───
  const m = useMemo(() => {
    const done = tareas.filter(t => t.estado === 'completada').length
    const cotizado = tareas.reduce((s, t) => s + (t.monto ?? 0), 0)
    const facturado = tareas.filter(t => t.factura_id != null).reduce((s, t) => s + (t.monto ?? 0), 0)
    const pendienteMonto = Math.max(0, cotizado - facturado)
    const horasReales = tareas.reduce((s, t) => s + (t.horas_reales ?? 0), 0)
    const abiertas = tareas.filter(t => t.estado !== 'completada').length
    const progreso = progresoProyecto(tareas)
    return { done, cotizado, facturado, pendienteMonto, horasReales, abiertas, progreso, total: tareas.length }
  }, [tareas])

  const bill = useMemo(() => (proyecto ? billingSummary(proyecto, tareas) : null), [proyecto, tareas])
  const fmt = (v: number) => formatCurrency(v, proyecto?.moneda)

  // ─── Gantt ───
  const ganttTasks = useMemo(() => {
    const base = filterEstado ? searchFiltered.filter(t => t.estado === filterEstado) : searchFiltered
    return [...base].sort((a, b) => (estadoOrder[a.estado] ?? 9) - (estadoOrder[b.estado] ?? 9))
  }, [searchFiltered, filterEstado])

  const { minDate, maxDate, totalDays } = useMemo(() => {
    const now = new Date()
    let min = new Date(now); let max = new Date(now)
    min.setMonth(min.getMonth() - 1); max.setMonth(max.getMonth() + 2)
    tareas.forEach(t => {
      const created = new Date(t.created_at)
      const deadline = t.fecha_limite ? new Date(t.fecha_limite) : null
      if (created < min) min = new Date(created)
      if (deadline && deadline > max) max = new Date(deadline)
    })
    const days = Math.max(Math.ceil((max.getTime() - min.getTime()) / (1000 * 60 * 60 * 24)), 30)
    return { minDate: min, maxDate: max, totalDays: days }
  }, [tareas])

  const monthMarkers = useMemo(() => {
    const markers: { label: string; left: number }[] = []
    const d = new Date(minDate); d.setDate(1)
    if (d < minDate) d.setMonth(d.getMonth() + 1)
    while (d <= maxDate) {
      const dayOffset = Math.ceil((d.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24))
      const pct = (dayOffset / totalDays) * 100
      const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
      markers.push({ label: `${monthNames[d.getMonth()]} ${d.getFullYear()}`, left: pct })
      d.setMonth(d.getMonth() + 1)
    }
    return markers
  }, [minDate, maxDate, totalDays])

  function getBarStyle(t: Tarea) {
    const start = new Date(t.created_at)
    const end = t.fecha_limite ? new Date(t.fecha_limite) : new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000)
    const startOffset = Math.max(0, (start.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24))
    const duration = Math.max(2, (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
    const left = (startOffset / totalDays) * 100
    const width = (duration / totalDays) * 100
    const color = estadoColors[t.estado] || '#636e72'
    const progress = t.estado === 'completada' ? 100 : t.estado === 'en_progreso' ? 50 : t.estado === 'bloqueada' ? 10 : 0
    return { left: `${left}%`, width: `${Math.max(width, 1.5)}%`, color, progress }
  }

  const todayPct = useMemo(() => {
    const now = new Date()
    const offset = (now.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24)
    return (offset / totalDays) * 100
  }, [minDate, totalDays])

  const selectedTaskData = selectedTask ? tareas.find(t => t.id === selectedTask) : null
  const selectedEvidencias = selectedTask ? evidencias.filter(e => e.tarea_id === selectedTask) : []
  const openTask = (tid: number) => setSelectedTask(prev => (prev === tid ? null : tid))
  const closePanel = () => setSelectedTask(null)
  const toggleInv = (key: string) => setOpenInv(prev => ({ ...prev, [key]: !prev[key] }))

  if (loading) {
    return (
      <div className="portal-loading-state">
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
        <p>Cargando proyecto...</p>
      </div>
    )
  }

  if (notFound || !proyecto) {
    return (
      <div className="pt-fact-empty" style={{ padding: '48px 24px' }}>
        <h3>Proyecto no encontrado</h3>
        <p>Este proyecto no existe o no pertenece a la empresa seleccionada.</p>
        <button className="btn btn-sm btn-secondary" style={{ marginTop: 12 }} onClick={() => navigate(indexPath)}>
          Volver a proyectos
        </button>
      </div>
    )
  }

  const estadoPb = proyectoEstadoBadge[proyecto.estado] ?? proyectoEstadoBadge.activo

  // Paginación de la tabla de tareas (15/página)
  const sortedTasks = [...tareas].sort((a, b) => (estadoOrder[a.estado] ?? 9) - (estadoOrder[b.estado] ?? 9))
  const taskPages = Math.max(1, Math.ceil(sortedTasks.length / PER_TASKS))
  const curTaskPage = Math.min(taskPage, taskPages)
  const pageTasks = sortedTasks.slice((curTaskPage - 1) * PER_TASKS, curTaskPage * PER_TASKS)

  const chevron = (
    <svg className="pd-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 6 6 6-6 6" /></svg>
  )

  // ─── Stat strip (según modelo de cobro) ───
  const stats: { label: string; value: React.ReactNode; sub?: string; color: string }[] = (() => {
    const avance = { label: 'Avance', value: <>{m.progreso}<small>%</small></>, sub: `${m.done} de ${m.total} tareas`, color: 'var(--accent-primary)' }
    if (bill?.modelo === 'proyecto') {
      const restante = Math.max(0, (proyecto.presupuesto ?? 0) - m.facturado)
      return [
        avance,
        { label: 'Facturado', value: fmt(m.facturado), color: 'var(--success)' },
        { label: 'Restante', value: fmt(restante), sub: 'por facturar', color: 'var(--warning)' },
        { label: 'Presupuesto', value: fmt(proyecto.presupuesto ?? 0), sub: 'precio cerrado', color: 'var(--info)' },
      ]
    }
    if (bill?.modelo === 'horas') {
      return [
        avance,
        { label: 'Horas registradas', value: <>{m.horasReales}<small> h</small></>, color: 'var(--primary-light)' },
        { label: 'Tareas abiertas', value: String(m.abiertas), color: 'var(--warning)' },
        { label: 'Completadas', value: String(m.done), sub: `de ${m.total}`, color: 'var(--success)' },
      ]
    }
    return [
      avance,
      { label: 'Facturado', value: fmt(m.facturado), sub: `${facturacionData.bloques.length} factura${facturacionData.bloques.length !== 1 ? 's' : ''}`, color: 'var(--success)' },
      { label: 'Pendiente', value: fmt(m.pendienteMonto), sub: `${facturacionData.pendientes.length} por facturar`, color: 'var(--warning)' },
      { label: 'Cotizado total', value: fmt(m.cotizado), sub: 'alcance', color: 'var(--info)' },
    ]
  })()

  const pendMonto = facturacionData.pendientes.reduce((s, t) => s + (t.monto ?? 0), 0)
  const factBase = bill?.modelo === 'proyecto' ? (proyecto.presupuesto ?? 0) : m.cotizado
  const factPct = factBase > 0 ? Math.min(100, (m.facturado / factBase) * 100) : 0
  const hayFacturacion = facturacionData.bloques.length > 0 || facturacionData.pendientes.length > 0

  return (
    <div className="pt-layout-v2">
      <button className="pd-back" onClick={() => navigate(indexPath)}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        Volver a proyectos
      </button>

      {/* ─── Header ─── */}
      <Breadcrumbs items={[{ label: 'Portal', to: '/portal' }, { label: 'Proyectos', to: '/portal/proyectos' }, { label: proyecto.nombre }]} />
      <div className="pd-header">
        <div className="pd-header-main">
          <div className="pd-title-row">
            <span className="pd-proyecto-nombre">{proyecto.nombre}</span>
            <span className={`badge ${estadoPb.className}`}>{estadoPb.label}</span>
            {bill && <span className="pd-model">{MODELO_LABEL[bill.modelo]}</span>}
          </div>
          <div className="pd-meta">
            <span className="m">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M8 2v4M16 2v4M3 10h18" /></svg>
              {proyecto.fecha_inicio ? formatDate(proyecto.fecha_inicio) : '—'} → {proyecto.fecha_fin_estimada ? formatDate(proyecto.fecha_fin_estimada) : '—'}
            </span>
            <span className="m">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
              <b>{m.done}</b> de {m.total} tareas
            </span>
          </div>
        </div>
        <div className="pd-ring" style={{ '--p': m.progreso } as React.CSSProperties}>
          <span className="p">{m.progreso}%</span>
          <span className="l">Avance</span>
        </div>
      </div>

      {/* ─── Stat strip ─── */}
      <div className="pd-stats">
        {stats.map((s, i) => (
          <div key={i} className="pd-stat" style={{ '--sc': s.color } as React.CSSProperties}>
            <div className="sl">{s.label}</div>
            <div className="sv">{s.value}</div>
            {s.sub && <div className="ss">{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* ─── Tabs ─── */}
      <div className="pt-tabs pd-tabs">
        {([['resumen', 'Resumen'], ['timeline', 'Línea de Tiempo'], ['facturacion', 'Facturación']] as [ViewTab, string][]).map(([v, label]) => (
          <button key={v} className={`pt-tab ${activeTab === v ? 'active' : ''}`} onClick={() => setActiveTab(v)}>{label}</button>
        ))}
      </div>

      {/* ─── Search + estado chips (solo en Línea de Tiempo) ─── */}
      {activeTab === 'timeline' && (
        <div className="pt-toolbar">
          <div className="pt-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input type="text" className="pt-search-input" placeholder="Buscar tareas..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            {searchQuery && <button className="pt-search-clear" onClick={() => setSearchQuery('')}>✕</button>}
          </div>
          <div className="pt-gantt-filters">
            <div className="pt-filter-chips">
              {[
                { value: '', label: 'Todos' },
                { value: 'pendiente', label: 'Backlog' },
                { value: 'en_progreso', label: 'En Curso' },
                { value: 'completada', label: 'Completadas' },
                { value: 'bloqueada', label: 'Bloqueadas' },
              ].map(opt => (
                <button key={opt.value} className={`pt-filter-chip ${filterEstado === opt.value ? 'active' : ''}`} onClick={() => setFilterEstado(opt.value)}>
                  {opt.value && <span className="pt-filter-chip-dot" style={{ background: estadoColors[opt.value] || 'var(--text-muted)' }} />}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ─── Content ─── */}
      <div className="pt-content-v2">
        {/* === RESUMEN === */}
        {activeTab === 'resumen' && (
          <div className="pd-resumen">
            <div className="pd-resumen-main">
              <div className="pd-card">
                <div className="pd-card-head"><h3>Tareas</h3><span className="sub">{m.done} / {m.total} completadas</span></div>
                {tareas.length === 0 ? (
                  <div className="pd-card-body"><p className="pd-desc">Este proyecto aún no tiene tareas.</p></div>
                ) : (
                  <>
                    <table className="pd-tt">
                      <thead><tr><th>Tarea</th><th>Estado</th><th>Prioridad</th><th className="pd-r">Horas</th><th className="pd-r">Monto</th></tr></thead>
                      <tbody>
                        {pageTasks.map(t => {
                          const horas = t.horas_reales > 0 ? t.horas_reales : t.horas_estimadas
                          return (
                            <tr key={t.id} onClick={() => openTask(t.id)}>
                              <td><div className="tk"><span className={`ci ${ciCls[t.estado] ?? ''}`}>{t.estado === 'completada' ? '✓' : ''}</span>{t.titulo}</div></td>
                              <td><span className={`pd-st ${stCls[t.estado] ?? 'pend'}`}><span className="dot" />{stLbl[t.estado] ?? t.estado}</span></td>
                              <td><span className={`pd-prio ${t.prioridad}`}>{t.prioridad}</span></td>
                              <td className="pd-r">{horas > 0 ? <span className="pd-hrs">{horas} h</span> : <span className="pd-muted">—</span>}</td>
                              <td className="pd-r">{t.monto > 0 ? <span className="pd-money">{fmt(t.monto)}</span> : <span className="pd-muted">—</span>}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                    {taskPages > 1 && (
                      <div className="pd-pager">
                        <span>Mostrando {(curTaskPage - 1) * PER_TASKS + 1}–{Math.min(curTaskPage * PER_TASKS, sortedTasks.length)} de {sortedTasks.length} tareas</span>
                        <span className="pnav">
                          <button disabled={curTaskPage === 1} onClick={() => setTaskPage(curTaskPage - 1)}>‹ Anterior</button>
                          <span className="pnum">{curTaskPage} / {taskPages}</span>
                          <button disabled={curTaskPage === taskPages} onClick={() => setTaskPage(curTaskPage + 1)}>Siguiente ›</button>
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="pd-resumen-side">
              {bill && <BillingCard bill={bill} presupuesto={proyecto.presupuesto} m={m} fmt={fmt} />}
              <div className="pd-card">
                <div className="pd-card-head"><h3>Detalles</h3></div>
                <div className="pd-card-body"><div className="pd-kv">
                  <div className="pd-kv-row"><span>Modelo de cobro</span><b>{bill ? MODELO_LABEL[bill.modelo] : '—'}</b></div>
                  <div className="pd-kv-row"><span>Moneda</span><b>{proyecto.moneda}</b></div>
                  {proyecto.fecha_inicio && <div className="pd-kv-row"><span>Inicio</span><b>{formatDate(proyecto.fecha_inicio)}</b></div>}
                  {proyecto.fecha_fin_estimada && <div className="pd-kv-row"><span>Fin estimado</span><b>{formatDate(proyecto.fecha_fin_estimada)}</b></div>}
                </div></div>
              </div>
              {proyecto.descripcion && (
                <div className="pd-card">
                  <div className="pd-card-head"><h3>Descripción</h3></div>
                  <div className="pd-card-body"><p className="pd-desc">{proyecto.descripcion}</p></div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* === TIMELINE === */}
        {activeTab === 'timeline' && (
          <div className="pt-gantt-chart" ref={ganttRef}>
            <div className="pt-gantt-months">
              {monthMarkers.map((mk, i) => (<div key={i} className="pt-gantt-month" style={{ left: `${mk.left}%` }}>{mk.label}</div>))}
            </div>
            <div className="pt-gantt-body">
              {todayPct >= 0 && todayPct <= 100 && (
                <div className="pt-gantt-today" style={{ left: `${todayPct}%` }}><span className="pt-gantt-today-label">Hoy</span></div>
              )}
              {monthMarkers.map((mk, i) => (<div key={i} className="pt-gantt-gridline" style={{ left: `${mk.left}%` }} />))}
              {ganttTasks.length === 0 ? (
                <div className="pt-gantt-empty">Sin tareas para mostrar</div>
              ) : (
                ganttTasks.map(t => {
                  const bar = getBarStyle(t)
                  const isSelected = selectedTask === t.id
                  return (
                    <div key={t.id} className="pt-gantt-row">
                      <div className="pt-gantt-row-label" title={t.titulo}>{t.titulo}</div>
                      <div className="pt-gantt-row-track">
                        <div
                          className={`pt-gantt-bar ${isSelected ? 'selected' : ''}`}
                          style={{ left: bar.left, width: bar.width, '--bar-color': bar.color, '--bar-progress': `${bar.progress}%` } as React.CSSProperties}
                          onClick={() => openTask(t.id)}
                          title={`${t.titulo} — ${estadoLabel[t.estado]?.label || t.estado}`}
                        >
                          <div className="pt-gantt-bar-fill" />
                          {parseFloat(bar.width) > 4 && <span className="pt-gantt-bar-label">{bar.progress}%</span>}
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
            <div className="pt-gantt-legend">
              {Object.entries(estadoColors).map(([key, color]) => (
                <div key={key} className="pt-gantt-legend-item">
                  <span className="pt-gantt-legend-dot" style={{ background: color }} />
                  {estadoLabel[key]?.label || key}
                </div>
              ))}
              <div className="pt-gantt-legend-item">
                <span className="pt-gantt-legend-dot" style={{ background: 'var(--warning)', borderRadius: 0, width: 2, height: 12 }} />
                Hoy
              </div>
            </div>
          </div>
        )}

        {/* === FACTURACIÓN === */}
        {activeTab === 'facturacion' && (
          <div>
            {!hayFacturacion ? (
              <div className="pt-fact-empty">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--border-light)" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                <h3>Sin facturación aún</h3>
                <p>Las tareas completadas y facturadas aparecerán aquí.</p>
              </div>
            ) : (
              <>
                <div className="pd-fsum">
                  <div className="nums">
                    {bill?.modelo === 'proyecto'
                      ? <><span>Presupuesto <b>{fmt(proyecto.presupuesto ?? 0)}</b></span><span>Facturado <b>{fmt(m.facturado)}</b></span><span>Restante <b>{fmt(Math.max(0, (proyecto.presupuesto ?? 0) - m.facturado))}</b></span></>
                      : <><span>Cotizado <b>{fmt(m.cotizado)}</b></span><span>Facturado <b>{fmt(m.facturado)}</b></span><span>Pendiente <b>{fmt(m.pendienteMonto)}</b></span></>}
                  </div>
                  <span className="pct">{Math.round(factPct)}% facturado</span>
                </div>
                <div className="pd-track" style={{ height: 7, marginBottom: 18 }}><i style={{ width: `${factPct}%` }} /></div>

                <div className="pd-card">
                  <table className="pd-ftable">
                    <thead><tr><th>Documento</th><th className="pd-r">Tareas</th><th>Estado</th><th className="pd-r">Monto</th><th /></tr></thead>
                    <tbody>
                      {facturacionData.pendientes.length > 0 && (
                        <>
                          <tr className={`pd-frow ${openInv['pend'] ? 'open' : ''}`} onClick={() => toggleInv('pend')}>
                            <td><div className="doc"><span className="di" style={{ background: 'var(--warning)' }} /><div>Pendiente por facturar<small>Tareas completadas sin factura</small></div></div></td>
                            <td className="pd-r">{facturacionData.pendientes.length}</td>
                            <td><span className="pd-st pend"><span className="dot" />Por facturar</span></td>
                            <td className="pd-r pd-money">{fmt(pendMonto)}</td>
                            <td className="pd-r">{chevron}</td>
                          </tr>
                          <tr className={`pd-drow ${openInv['pend'] ? 'show' : ''}`}><td colSpan={5}><div className="pd-dwrap"><div className="pd-dinner">
                            <table className="pd-ditems"><tbody>
                              {facturacionData.pendientes.map(t => (
                                <tr key={t.id}><td>{t.titulo}</td><td className="pd-r">{t.horas_reales > 0 ? `${t.horas_reales} h` : '—'}</td><td className="pd-r">{fmt(t.monto ?? 0)}</td></tr>
                              ))}
                              <tr><td className="ft">Subtotal pendiente</td><td /><td className="pd-r ft">{fmt(pendMonto)}</td></tr>
                            </tbody></table>
                          </div></div></td></tr>
                        </>
                      )}
                      {facturacionData.bloques.map(b => {
                        const key = `f${b.facturaId}`
                        return (
                          <Fragment key={key}>
                            <tr className={`pd-frow ${openInv[key] ? 'open' : ''}`} onClick={() => toggleInv(key)}>
                              <td><div className="doc"><span className="di" style={{ background: 'var(--success)' }} /><div>Factura #{b.facturaId}<small>{b.tasks.length} tarea{b.tasks.length !== 1 ? 's' : ''}{b.horas > 0 ? ` · ${b.horas} h` : ''}</small></div></div></td>
                              <td className="pd-r">{b.tasks.length}</td>
                              <td><span className="pd-st done"><span className="dot" />Facturada</span></td>
                              <td className="pd-r pd-money">{fmt(b.total)}</td>
                              <td className="pd-r">{chevron}</td>
                            </tr>
                            <tr className={`pd-drow ${openInv[key] ? 'show' : ''}`}><td colSpan={5}><div className="pd-dwrap"><div className="pd-dinner">
                              <table className="pd-ditems"><tbody>
                                {b.tasks.map(t => (
                                  <tr key={t.id}><td>{t.titulo}</td><td className="pd-r">{t.horas_reales > 0 ? `${t.horas_reales} h` : '—'}</td><td className="pd-r">{fmt(t.monto ?? 0)}</td></tr>
                                ))}
                                <tr><td className="ft">Total factura #{b.facturaId}</td><td /><td className="pd-r ft">{fmt(b.total)}</td></tr>
                              </tbody></table>
                            </div></div></td></tr>
                          </Fragment>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ─── Slide-over ─── */}
      {selectedTaskData && (
        <>
          <div className="pt-overlay" onClick={closePanel} />
          <aside className="pt-slideover">
            <div className="pt-slideover-header">
              <h3 className="pt-slideover-title">{selectedTaskData.titulo}</h3>
              <button className="pt-slideover-close" onClick={closePanel}>✕</button>
            </div>
            <div className="pt-slideover-body">
              <div className="pt-slideover-badges">
                <span className={`badge ${(estadoLabel[selectedTaskData.estado] ?? estadoLabel.pendiente).className}`}>
                  {(estadoLabel[selectedTaskData.estado] ?? estadoLabel.pendiente).label}
                </span>
                <span className={`pt-panel-priority ${(prioridadLabel[selectedTaskData.prioridad] ?? prioridadLabel.media).className}`}>
                  {(prioridadLabel[selectedTaskData.prioridad] ?? prioridadLabel.media).label}
                </span>
              </div>
              {selectedTaskData.descripcion && (
                <div className="pt-slideover-field">
                  <span className="pt-slideover-label">Descripción</span>
                  <p className="pt-slideover-desc">{selectedTaskData.descripcion}</p>
                </div>
              )}
              <div className="pt-slideover-row">
                {selectedTaskData.created_at && (
                  <div className="pt-slideover-field">
                    <span className="pt-slideover-label">Creada</span>
                    <span>{formatDate(selectedTaskData.created_at)}</span>
                  </div>
                )}
                {selectedTaskData.fecha_limite && (
                  <div className="pt-slideover-field">
                    <span className="pt-slideover-label">Fecha Límite</span>
                    <span>{formatDate(selectedTaskData.fecha_limite)}</span>
                  </div>
                )}
              </div>
              <div className="pt-slideover-row">
                {selectedTaskData.horas_estimadas > 0 && (
                  <div className="pt-slideover-field">
                    <span className="pt-slideover-label">Horas Est.</span>
                    <span className="pt-slideover-highlight">{selectedTaskData.horas_estimadas}h</span>
                  </div>
                )}
                {selectedTaskData.monto > 0 && (
                  <div className="pt-slideover-field">
                    <span className="pt-slideover-label">Monto</span>
                    <span className="pt-slideover-highlight">{fmt(selectedTaskData.monto)}</span>
                  </div>
                )}
              </div>
              {selectedEvidencias.length > 0 && (
                <div className="pt-slideover-field">
                  <span className="pt-slideover-label">Evidencias ({selectedEvidencias.length})</span>
                  <div className="pt-slideover-evidencias">
                    {selectedEvidencias.map(ev => (
                      <a key={ev.id} href={ev.url} target="_blank" rel="noopener noreferrer" className="pt-slideover-evidence-link">
                        {ev.tipo?.includes('image') ? (
                          <img src={ev.url} alt={ev.nombre} className="pt-slideover-evidence-thumb" />
                        ) : (
                          <div className="pt-slideover-evidence-file">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                            <span>{ev.nombre}</span>
                          </div>
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  )
}

/* ─── Tarjeta de resumen de cobro (right rail), varía por modelo ─── */
function BillingCard({ bill, presupuesto, m, fmt }: {
  bill: ReturnType<typeof billingSummary>
  presupuesto: number
  m: { facturado: number; cotizado: number; pendienteMonto: number; horasReales: number }
  fmt: (v: number) => string
}) {
  if (bill.modelo === 'horas') {
    return (
      <div className="pd-card">
        <div className="pd-card-head"><h3>Horas registradas</h3><span className="pd-model">Paquete de horas</span></div>
        <div className="pd-card-body">
          <div className="pd-bill-figure">{m.horasReales}<small> h</small></div>
          <p className="pd-bill-note">Este proyecto se factura contra tu paquete mensual de horas. El saldo lo ves en “Horas / Plan”.</p>
        </div>
      </div>
    )
  }
  if (bill.modelo === 'proyecto') {
    const pct = presupuesto > 0 ? Math.min(100, (m.facturado / presupuesto) * 100) : 0
    return (
      <div className="pd-card">
        <div className="pd-card-head"><h3>Presupuesto</h3><span className="pd-model">Precio cerrado</span></div>
        <div className="pd-card-body">
          <div className="pd-bill-top"><span className="pd-bill-big">{fmt(m.facturado)}</span><span className="pd-bill-of">de {fmt(presupuesto)}</span></div>
          <div className="pd-track"><i style={{ width: `${pct}%` }} /></div>
          <p className="pd-bill-note">Facturado {Math.round(pct)}% · restante {fmt(Math.max(0, presupuesto - m.facturado))}.</p>
        </div>
      </div>
    )
  }
  return (
    <div className="pd-card">
      <div className="pd-card-head"><h3>Cobro por tarea</h3><span className="pd-model">Aprob. 1×1</span></div>
      <div className="pd-card-body">
        <div className="pd-bill-top"><span className="pd-bill-big">{fmt(m.facturado)}</span><span className="pd-bill-of">de {fmt(m.cotizado)} cotizado</span></div>
        <div className="pd-track"><i style={{ width: `${m.cotizado > 0 ? Math.min(100, (m.facturado / m.cotizado) * 100) : 0}%` }} /></div>
        <div className="pd-brk">
          <div className="row"><span className="lab"><span className="sw" style={{ background: 'var(--primary)' }} />Facturado</span><b>{fmt(m.facturado)}</b></div>
          <div className="row"><span className="lab"><span className="sw" style={{ background: 'var(--warning)' }} />Pendiente por facturar</span><b>{fmt(m.pendienteMonto)}</b></div>
          <div className="row"><span className="lab"><span className="sw" style={{ background: 'var(--bg-input)' }} />Cotizado total</span><b>{fmt(m.cotizado)}</b></div>
        </div>
      </div>
    </div>
  )
}
